import { useEffect, useState } from "react";

import withErrorHandling from "../HOC/ErrorHandling";
import { useSharedProps } from "../HOC/SharedProps";
import { getEnv } from "../helpers/env";

import {
  Visibility,
  VisibilityOff,
  ReportOutlined as ReportOutlinedIcon
} from "@mui/icons-material";

import {
  Step,
  Stepper,
  StepButton,
  Box,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  LinearProgress,
  Autocomplete,
  InputAdornment,
  IconButton,
  Tooltip,
} from "@mui/material";

import t from "../helpers/en";
import {
  getGitSecrets,
  setWizard3,
  getRegistrySecrets,
  getSecret,
  getConfigsAndSecrets,
  updateWizard3,
  getServiceCatalogByPath,
  getClustersInstances,
} from "../helpers/utils";
import {
  GIT_NEW_CREDENTIALS,
  REGISTRY_NEW_CREDENTIALS,
  PRIVATE,
  PRIVATE_REGISTRY,
  JSON_ADD_VARIABLES,
} from "../helpers/constants";
import {
  changeTxt,
  selectOption,
  configsAndSecretsShowPwd,
  setConfigsAndSecrets,
  changeSubTxt,
  setJsonVariable2Table,
  onValidateForm,
  onValidateFormStep,
  onValidateField,
} from "../helpers/selectors";

import Select from "../components/Inputs/Select";
import SetConfigsAndSecrets from "../components/SetConfigsAndSecrets";
import Loader from "../components/Loading";

import {
  loaderStyle,
  styleSteps,
  styleStepBtn,
  sectionLabel,
  styleQuestionLabel,
  styleQuestionAnswer,
} from "../GeneralStyles";

const PUBLIC = "PUBLIC";
const PLATFORM_REGISTRY = "PLATFORM_REGISTRY";
const ACCESS_TYPE = [
  { id: PUBLIC, name: t.public },
  { id: PRIVATE, name: t.private },
];
const REGISTRY_ACCESS_TYPE = [
  { id: PLATFORM_REGISTRY, name: t.platformHostedRegistry },
  { id: PRIVATE_REGISTRY, name: t.private },
];
const timezones = [
  { id: "PST", label: "PST" },
  { id: "CST", label: "CST" },
  { id: "EST", label: "EST" },
];

const MAIN_BRANCH = "main";
const CUSTOM_BRANCH = "custom";
const MASTER_BRANCH = "master";
const APPLICATION_SIZES = [
  { id: "TINY", label: "Tiny" },
  { id: "SMALL", label: "Small" },
  { id: "MEDIUM", label: "Medium" },
  { id: "LARGE", label: "Large" },
];

const INITIAL_STATE_FORM = {
  git_accessType: PUBLIC,
  registry_accessType: PLATFORM_REGISTRY,
  name: "system-default",
  deployAutomatically: true,
  scaleTo0: true,
  db_deploy: false,
  variables: [{ key: null, value: null }],
  secrets: [{ key: null, value: null, isPwdVisible: false }],
  configs_values: [],
  tabVal: 0,
  git_revision_selector: MAIN_BRANCH,
  git_revision: "main",
  instancesCatalog: [],
  cluster_name: "default",
  application_size: "SMALL",
};
const GIT_BRANCHES = [
  { id: MAIN_BRANCH, label: "Main" },
  { id: MASTER_BRANCH, label: "Master" },
  { id: CUSTOM_BRANCH, label: "Custom" },
];

const BasicsContent = ({ form, errors, onChangeTxt, onSelectOption, clusters }) => (
  <>
    <TextField
      id={`outlined-basic-name`}
      label={t.application + " " + t.name}
      name="displayName"
      value={form?.displayName || ""}
      onChange={onChangeTxt}
      InputProps={{
        readOnly: form?.isEditForm,
      }}
      size="small"
      required
      error={!!errors.displayName}
      helperText={errors.displayName}
    />

    { getEnv("REACT_APP_MULTICLUSTER_ENABLED") && (
      <Select
        options={clusters}
        onChange={(evt, val) => onSelectOption(evt, val, "cluster_name")}
        label={t.targetKubernetesCluster}
        item="name"
        value={
          clusters?.find((it) => it.id === form?.cluster_name) || ""
        }
        specificDetails={{
          required: true,
          disabled: form?.isEditForm,
          getOptionDisabled: (() => form?.isEditForm),
        }}
      />
    )}
  </>
);

const GitComponent = ({
  form,
  onChangeTxt,
  onSelectOption,
  gitCredentials,
  showCredentials = true,
  onToggleShowSecret,
  errors,
}) => (
  <>
    <TextField
      id="outlined-basic-git_repo"
      label={t.git + " " + t.repository}
      name="git_repo"
      value={form?.git_repo || ""}
      onChange={onChangeTxt}
      InputProps={{
        readOnly: form?.isEditForm,
      }}
      size="small"
      required
      error={!!errors.git_repo}
      helperText={errors.git_repo}
    />
    <Select
      options={GIT_BRANCHES}
      onChange={(evt, val) => onSelectOption(evt, val, "git_revision_selector")}
      label={t.git + " " + t.revision}
      item="label"
      value={
        GIT_BRANCHES?.find((it) => it.id === form?.git_revision_selector) || ""
      }
      specificDetails={{
        errors,
        required: true,
        errorField: "git_revision_selector",
      }}
    />
    {form?.git_revision_selector === CUSTOM_BRANCH && (
      <TextField
        id="outlined-basic-git_revison"
        label={`Type in the input custom ${t.git + " " + t.revision}`}
        name="git_revision"
        value={form?.git_revision || ""}
        onChange={onChangeTxt}
        size="small"
        required
        error={!!errors.git_revision}
      />
    )}
    {showCredentials && (
      <Select
        options={ACCESS_TYPE}
        onChange={(evt, val) => onSelectOption(evt, val, "git_accessType")}
        label={t.git + " " + t.accessType}
        item="name"
        value={ACCESS_TYPE?.find((it) => it.id === form?.git_accessType) || ""}
      />
    )}

    {form?.git_accessType === PRIVATE && (
      <Select
        options={gitCredentials}
        onChange={(evt, val) => onSelectOption(evt, val, "git_secrets")}
        label={t.privateRepository}
        item="name"
        value={gitCredentials?.find((it) => it.id === form?.git_secrets) || ""}
        specificDetails={{
          errors,
          required: true,
          errorField: "git_secrets",
          styles: {
            elemLi: (el) =>
              (el?.default || el?.id === GIT_NEW_CREDENTIALS) && {
                borderTop: "1px solid rgba(0,0,0,0.3)",
              },
          },
        }}
      />
    )}
    {form?.git_secrets === GIT_NEW_CREDENTIALS &&
      form?.git_accessType === PRIVATE && (
        <Box sx={{ display: "grid", gridRowGap: "1rem", ml: "1.5rem" }}>
          <TextField
            id="outlined-basic-git_secrets_username"
            label={t.username}
            name="git_cred_username"
            value={form?.git_cred_username || ""}
            onChange={onChangeTxt}
            size="small"
            required
            error={!!errors.git_cred_username}
            helperText={errors.git_cred_username}
          />
          <TextField
            id="outlined-basic-git_secrets_password"
            label={"Secret"}
            name="git_cred_password"
            type={form?.showGitSecret ? "text" : "password"}
            value={form.git_cred_password || ""}
            onChange={onChangeTxt}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => onToggleShowSecret("showGitSecret")}
                    edge="end"
                  >
                    {form?.showGitSecret ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            size="small"
            required
            error={!!errors.git_cred_password}
            helperText={errors.git_cred_password}
          />
        </Box>
      )}
  </>
);

const RegistryComponent = ({
  registryCredentials,
  onSelectOption,
  form,
  onChangeTxt,
  onToggleShowSecret,
  errors,
}) => (
  <>
    <Select
      options={REGISTRY_ACCESS_TYPE}
      onChange={(evt, val) => onSelectOption(evt, val, "registry_accessType")}
      label={`${t.registry} ${t.accessType}`}
      item="name"
      value={
        REGISTRY_ACCESS_TYPE?.find(
          (it) => it.id === form?.registry_accessType
        ) || ""
      }
    />
    {form?.registry_accessType === PRIVATE_REGISTRY && (
      <Select
        options={registryCredentials}
        onChange={(evt, val) => onSelectOption(evt, val, "registry_secrets")}
        label={t.registry}
        item="name"
        value={
          registryCredentials?.find((it) => it.id === form?.registry_secrets) ||
          ""
        }
        specificDetails={{
          errors,
          required: true,
          errorField: "registry_secrets",
          styles: {
            elemLi: (el) =>
              (el?.default || el?.id === REGISTRY_NEW_CREDENTIALS) && {
                borderTop: "1px solid rgba(0,0,0,.3)",
              },
          },
        }}
      />
    )}
    {form?.registry_accessType === PRIVATE_REGISTRY &&
      form?.registry_secrets === REGISTRY_NEW_CREDENTIALS && (
        <Box
          sx={{
            display: "grid",
            gridRowGap: "1rem",
            border: "1px solid rgba(0,0,0,0.2)",
            borderRadius: "0.4rem",
            p: "1rem",
          }}
        >
          <TextField
            id={`outlined-registry_name`}
            label={`${t.registry} ${t.name}`}
            name="registry_name"
            value={form?.registry_name || ""}
            onChange={onChangeTxt}
            size="small"
          />
          <TextField
            id="outlined-basic-registry_secrets_server"
            label={t.server}
            name="registry_cred_server"
            value={form?.registry_cred_server || ""}
            onChange={onChangeTxt}
            size="small"
          />
          {form?.registry_cred_server?.includes(".amazonaws.com") && (
            <TextField
              id="outlined-basic-registry_secrets_region"
              label={t.region}
              name="registry_cred_region"
              value={form?.registry_cred_region || ""}
              onChange={onChangeTxt}
              size="small"
            />
          )}
          <TextField
            id="outlined-basic-registry_secrets_username"
            label={t.username}
            name="registry_cred_username"
            value={form?.registry_cred_username || ""}
            onChange={onChangeTxt}
            size="small"
            required
            error={!!errors.registry_cred_username}
            helperText={errors.registry_cred_username}
          />
          <TextField
            id="outlined-basic-registry_secrets_password"
            label={"Secret"}
            name="registry_cred_password"
            type={form?.showRegistrySecret ? "text" : "password"}
            value={form.registry_cred_password || ""}
            onChange={onChangeTxt}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => onToggleShowSecret("showRegistrySecret")}
                    edge="end"
                  >
                    {form?.showRegistrySecret ? (
                      <VisibilityOff />
                    ) : (
                      <Visibility />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            size="small"
            required
            error={!!errors.registry_cred_password}
            helperText={errors.registry_cred_password}
          />
        </Box>
      )}
  </>
);

const ServiceComponent = ({
  form,
  onChangeTxt,
  onCheckboxChange,
  configs,
  onSelectMultipleOptions,
  onChangeTab,
  onChangeSubTxt,
  onSetConfigsAndSecrets,
  onOpenSecond,
  onShowPwd,
  onChangeTxtJson,
  onSetJsonVariable2Table,
  onHideModalSecond,
  onSelectOption,
  instancesCatalog,
  onChangeInstances,
  errors,
}) => (
  <>
    <FormControlLabel
      label={t.deployAutomatically}
      control={
        <Checkbox
          checked={form.deployAutomatically}
          onChange={onCheckboxChange}
          name="deployAutomatically"
        />
      }
    />
    {form.deployAutomatically && (
      <span>
        <TextField
          id="outlined-basic-container_port"
          label={t.containerPort}
          name="container_port"
          value={form?.container_port || ""}
          onChange={onChangeTxt}
          size="small"
          required
          error={!!errors?.container_port}
        />
        <Tooltip title="This is the port on which the application runs. Usually for React applications the default port is 3000 and for Java it is 8080. For other languages, it is necessary to see the documentation." arrow>
          <ReportOutlinedIcon sx={{ fill: "#ff9800 !important",  marginLeft: "5px", marginTop: "5px" }} />
        </Tooltip>
      </span>
    )}
    <Select // CONFIGS & SECRETS TOGETHER
      multiple
      options={configs}
      onChange={(evt, val) =>
        onSelectMultipleOptions(evt, val, "configs_values") || []
      }
      label={t.configsAndSecrets}
      item="displayName"
      value={form?.configs_values}
    />

    <Autocomplete
      multiple
      options={instancesCatalog.flatMap((group) => group.resources)} // Flatten the grouped options into a single array
      groupBy={(option) => {
        // Grouping options based on `serviceCatalogName`
        const group = instancesCatalog.find((gr) =>
          gr.resources.some((r) => r.name === option.name)
        );

        return group?.serviceCatalogName || ""; // Return the group label
      }}
      value={form?.instancesCatalog}
      onChange={onChangeInstances}
      disableCloseOnSelect
      renderInput={(params) => <TextField {...params} label="Bind Services" />}
      isOptionEqualToValue={(option, value) => option.name === value.name} // Compare resources by `name`
      getOptionLabel={(option) => option.displayName}
      size="small"
    />

    <SetConfigsAndSecrets
      {...{
        form,
        onChangeTab,
        onChangeSubTxt,
        onSetConfigsAndSecrets,
        onOpenSecond,
        onShowPwd,
        onChangeTxtJson,
        onSetJsonVariable2Table,
        onHideModalSecond,
      }}
    />
    <FormControlLabel
      label={t.scaleTo0}
      control={
        <Checkbox
          checked={form.scaleTo0}
          onChange={onCheckboxChange}
          name="scaleTo0"
        />
      }
    />
    {form.scaleTo0 && (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: "1rem",
        }}
      >
        <TextField
          label="Up Time"
          type="time"
          name="up_time"
          value={form?.up_time || ""}
          onChange={onChangeTxt}
          InputLabelProps={{
            shrink: true,
          }}
          InputProps={{
            inputMode: "numeric",
            pattern: "[0-9]*",
          }}
          size="small"
          required
          error={!!errors.up_time}
          helperText={errors.up_time}
        />
        <TextField
          label="Down Time"
          type="time"
          name="down_time"
          value={form?.down_time || ""}
          onChange={onChangeTxt}
          InputLabelProps={{
            shrink: true,
          }}
          InputProps={{
            inputMode: "numeric",
            pattern: "[0-9]*",
          }}
          size="small"
          required
          error={!!errors.down_time}
          helperText={errors.down_time}
        />
        <Select
          options={timezones}
          onChange={(evt, val) => onSelectOption(evt, val, "timezone")}
          label={"Timezone"}
          item="label"
          value={timezones?.find((it) => it.id === form?.timezone) || ""}
          specificDetails={{
            errors,
            required: true,
            errorField: "timezone",
          }}
        />
      </div>
    )}

    <Select // Application Size
      options={APPLICATION_SIZES}
      onChange={(evt, val) => onSelectOption(evt, val, "application_size")}
      label={t.applicationSize}
      item="label"
      value={
        APPLICATION_SIZES?.find((it) => it.id === form?.application_size) || ""
      }
      specificDetails={{
        required: true,
//        disabled: form?.isEditForm,
//        getOptionDisabled: (() => form?.isEditForm),
      }}
    />
  </>
);

const ConfigureBuild = (props) => {
  const {
    showError,
    triggerUpdateTable,
    handleClose,
    data,
    currentUser,
    onDetectFormChange,
  } = props;

  const [form, setForm] = useState({
    ...INITIAL_STATE_FORM,
  });
  const [configs, setConfigs] = useState([]);
  const [gitCredentials, setGitCredentials] = useState([]);
  const [instancesCatalog, setInstancesCatalog] = useState([]);
  const [registryCredentials, setRegistryCredentials] = useState({});
  const [clusters, setClusters] = useState([]);

  const [stepState, setStepState] = useState({
    activeStep: 0,
    progress: 100,
    isLoading: false,
    opacity: 0,
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  const { currentGroup } = useSharedProps();

  const steps = [
    t.basicInfo,
    t.sourceInfo,
    t.dockerImage,
    t.applicationInfo,
    data?.isEditForm ? t.reviewPlusUpdate : t.reviewPlusCreate,
  ];

  const fetchGitCredential = () => {
    return new Promise((resolve, reject) => {
      getGitSecrets((result, err) => {
        if (Array.isArray(result)) {
          const updatedResult = [
            ...result.map((it) => ({
              git_cred_username: it?.data?.username,
              git_cred_password: it?.data?.password,
              id: it?.metadata?.name,
              name: it?.metadata?.displayName,
              default: Boolean(it?.metadata?.labels?.default),
            })),
            { id: GIT_NEW_CREDENTIALS, name: t.newCredentials },
          ];

          resolve(updatedResult);
        } else {
          reject(err);
        }
      });
    });
  };
  const fetchRegistryCredential = () => {
    return new Promise((resolve, reject) => {
      getRegistrySecrets((result, err) => {
        if (Array.isArray(result)) {
          const updatedResult = [
            ...result.map((it) => ({
              registry_cred_server: it?.data?.server,
              registry_cred_region: it?.metadata?.labels?.region,
              registry_cred_username: it?.data?.username,
              registry_cred_password: it?.data?.password,
              id: it?.metadata?.name,
              name: it?.metadata?.displayName,
              default: Boolean(it?.metadata?.labels?.default),
            })),
            { id: REGISTRY_NEW_CREDENTIALS, name: t.newRegistry },
          ];

          resolve(updatedResult);
        } else {
          reject(err);
        }
      });
    });
  };
  const fetchRegistrySecrets = () => {
    return new Promise((resolve, reject) => {
      getConfigsAndSecrets((result, err) => {
        if (Array.isArray(result)) {
          const updatedResult = result.map((it) => ({
            displayName: it?.displayName,
            name: it?.name,
          }));

          resolve(updatedResult);
        } else {
          reject(err);
        }
      });
    });
  };
  const fetchInstances = () => {
    return new Promise((resolve, reject) => {
      getServiceCatalogByPath(null, (result, err) => {
        if (Array.isArray(result)) {
          const updatedResult = result.map((it) => ({
            serviceCatalogName: it.serviceCatalogName,
            resources: it.resources?.map((r) => ({
              name: r.metadata.labels?.["serviceCatalog.bindingName"],
              displayName:
                r.metadata.labels?.["serviceCatalog.bindingDisplayName"],
            })),
          }));

          resolve(updatedResult);
        } else {
          reject(err);
        }
      });
    });
  };
  const fetchClustersInstances = () => {
    return new Promise((resolve, reject) => {
      getClustersInstances((result, err) => {
        if (Array.isArray(result)) {
          const updatedResult = [
            { id: "default", name: "Default" },
            ...result.map((it) => ({ 
              id: it.metadata.name,
              name: it.metadata.displayName,
            })),
          ];

          resolve(updatedResult);
        } else {
          reject(err);
        }
      });
    });
  };

  useEffect(() => {
    setIsLoading(true);

    Promise.all([
      fetchGitCredential(),
      fetchRegistryCredential(),
      fetchRegistrySecrets(),
      fetchInstances(),
      fetchClustersInstances(),
    ])
      .then(([gitResult, registryResult, configResult, instancesCatalog, clustersResult]) => {
        if (Array.isArray(gitResult)) {
          setGitCredentials(gitResult);
        }

        if (Array.isArray(registryResult)) {
          setRegistryCredentials(registryResult);
        }

        if (Array.isArray(configResult)) {
          setConfigs(configResult);
        }

        if (Array.isArray(instancesCatalog)) {
          setInstancesCatalog(instancesCatalog);
        }

        if (Array.isArray(clustersResult)) {
          setClusters(clustersResult);
        }

        if (data?.isEditForm || data?.isSetJson) {
          setForm({
            displayName: data?.displayName,
            git_repo: data.git?.repo,
            git_revision_selector:
              data.git?.revision === MAIN_BRANCH ||
              data.git?.revision === MASTER_BRANCH
                ? data.git?.revision
                : CUSTOM_BRANCH,
            git_revision: data.git?.revision,
            git_accessType: data.git?.accessType,
            git_secrets: data.git?.secrets?.name,
            git_cred_name: data.git?.secrets?.name,
            registry_cred_name: data.imagePullSecrets?.name,
            registry_accessType: data.imagePullSecrets?.registryType,
            registry_secrets: data.imagePullSecrets?.name,
            registry_cred_server: data.imagePullSecrets?.server,
            registry_cred_region: data.imagePullSecrets?.region,
            deployAutomatically: data.deployAutomatically,
            container_port: data.service?.containerPort,
            variables: Object.entries(data.service?.envParams || {})?.map(
              ([key, val]) => ({ key, value: val })
            ),
            secrets: Object.entries(data.service?.secretParams || {})?.map(
              ([key, val]) => ({ key, value: val })
            ),
            configs_values: configResult.filter((it) =>
              (data.service?.envFrom || []).some((el) => el === it.name)
            ),
            scaleTo0: data.service?.scaleTo0,
            db_deploy: false,
            tabVal: 0,
            up_time: formatTime(
              data.service?.dailyScaling?.up?.[0] || 0,
              data.service?.dailyScaling?.up?.[1] || 0
            ),
            down_time: formatTime(
              data.service?.dailyScaling?.down?.[0] || 0,
              data.service?.dailyScaling?.down?.[1] || 0
            ),
            timezone: data.service?.dailyScaling?.timezone,
            isEditForm: data.isEditForm,
            instancesCatalog: data?.service?.envVars?.serviceBinding?.bindings
              ?.map((it) =>
                instancesCatalog
                  ?.flatMap((group) => group?.resources)
                  ?.find((cat) => cat?.name === it)
              )
              ?.filter((item) => item !== undefined),
            cluster_name: data.clusterName,
            application_size: data?.service?.applicationSize,
          });
        }
      })
      .catch((err) => {
        showError(err);
      })
      .finally(() => setIsLoading(false));
  }, [data, showError, currentGroup]);

  const onStepNext = () => {
    const formFields = getRequiredFields();
    const validationErrors = onValidateFormStep({
      ...form,
      ...formFields,
    }, stepState?.activeStep);

    if (validationErrors && Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      showError(t.requiredMsg);
      return;
    }

    onStep(stepState?.activeStep + 1);
  };

  const onStepBack = () => {
    onStep(stepState?.activeStep - 1);
  };

  useEffect(() => {
    let interval;

    if (stepState.isLoading) {
      setStepState((prev) => ({ ...prev, opacity: 0 })); // Reset opacity to 0 when starting progress

      interval = setInterval(() => {
        setStepState((prev) => {
          const newProgress = prev.progress + 5;

          if (newProgress >= 100) {
            clearInterval(interval);

            return { ...prev, isLoading: false, progress: 100 };
          }

          return { ...prev, progress: newProgress };
        });
      }, 30);
    }

    return () => clearInterval(interval);
  }, [stepState.isLoading]);

  useEffect(() => {
    if (stepState.progress >= 10) {
      setStepState((prev) => ({
        ...prev,
        opacity: (stepState.progress - 10) / 90, // Gradually increase opacity from 10% to 100%
      }));
    }
  }, [stepState.progress]);

  const onStep = (step) => {
    if (stepState.activeStep === step) return;

    setStepState({
      ...stepState,
      activeStep: step,
      progress: 0,
      opacity: 0,
      isLoading: true,
    });
  };

  const onChangeTxt = (evt) => {
    const result = changeTxt(evt, form);
    const formFields = getRequiredFields();

    setForm(result);
    onDetectFormChange();
    // Validate the field
    setErrors(
      onValidateField(
        {
          ...result,
          ...formFields,
        },
        errors,
        evt.target.name
      )
    );
  };

  const onSelectOption = (_, value, param) => {
    let result = selectOption(_, value?.id ?? null, param, form);
    const formFields = getRequiredFields();

    if (param === "git_secrets") {
      result = {
        ...result,
        git_cred_username: value?.git_cred_username,
        git_cred_password: value?.git_cred_password,
        git_cred_name: value?.id,
      };
    } else if (param === "registry_secrets") {
      result = {
        ...result,
        registry_cred_username: value?.registry_cred_username,
        registry_cred_password: value?.registry_cred_password,
        registry_cred_server: value?.registry_cred_server,
        registry_cred_region: value?.registry_cred_region,
        registry_cred_name: value?.id,
      };
    } else if (param === "git_revision_selector") {
      result = {
        ...result,
        git_revision: value?.id === CUSTOM_BRANCH ? "" : value?.id,
      };
    }

    setForm(result);
    onDetectFormChange();
    // Validate the field
    setErrors(
      onValidateField(
        {
          ...result,
          ...formFields,
        },
        errors,
        param
      )
    );
  };

  const onCheckboxChange = (evt) => {
    const { name, checked } = evt.target;

    setForm({ ...form, [name]: checked });
    onDetectFormChange();
  };

  const onSelectMultipleOptions = (event, values, param) => {
    let cpy = [...(form?.[param] ?? [])];
    cpy = values;
    setForm({ ...form, [param]: cpy });
    onDetectFormChange();
  };

  const onChangeTab = (_, val) => {
    setForm((prev) => ({ ...prev, tabVal: val }));
  };

  const onShowPwd = async (e, elem) => {
    const result = await configsAndSecretsShowPwd(e, {
      ...elem,
      showError,
      getSecret,
    });

    setForm(result);
  };

  const onSetConfigsAndSecrets = (evt, elem) => {
    const result = setConfigsAndSecrets(evt, elem);

    setForm(result);
    onDetectFormChange();
  };

  const onChangeTxtJson = (e) => {
    const value = e.target.value;

    setForm((prev) => ({
      ...prev,
      ...{ secondModal: { ...prev.secondModal, value } },
    }));
    onDetectFormChange();
  };

  const onSetJsonVariable2Table = () => {
    const result = setJsonVariable2Table(form);

    setForm(result);
    onDetectFormChange();
  };

  const onChangeSubTxt = (evt, val, valIdx, param) => {
    const result = changeSubTxt(evt, form, valIdx, param);

    setForm(result);
    onDetectFormChange();
  };

  const onOpenSecond = (evt, param) => {
    const { id } = evt.target;
    let cpy = { ...form };
    const envParams = form?.[param]?.reduce((acc, currentVal) => {
      return {
        ...acc,
        ...(currentVal.key ? { [currentVal.key]: currentVal.value } : {}),
      };
    }, {});

    cpy = {
      ...cpy,
      secondModal: {
        open: true,
        ...(id === JSON_ADD_VARIABLES
          ? { value: JSON.stringify(envParams, null, 2) }
          : {}),
      },
    };

    setForm(cpy);
  };

  const onHideModalSecond = () => {
    let cpy = { ...form };

    cpy.secondModal = null;

    setForm(cpy);
  };

  const formatTime = (hour, min) => {
    return `${hour.toString().padStart(2, "0")}:${min
      .toString()
      .padStart(2, "0")}`;
  };

  const onChangeInstances = (evt, newValue) => {
    setForm((prev) => ({ ...prev, instancesCatalog: newValue }));
    onDetectFormChange();
  };

  const getRequiredFields = () => {
    const gitFields = {};

    // Git Access Type & Secrets
    if (form?.git_accessType === PRIVATE) {
      gitFields.isGitSecretsRequired = true;
    }

    if (
      form?.git_secrets === GIT_NEW_CREDENTIALS &&
      form?.git_accessType === PRIVATE
    ) {
      gitFields.isGitCredUsernameRequired = true;
      gitFields.isGitCredPasswordRequired = true;
    }

    // Registry Access Type & Secrets
    const registryFields = {};

    if (form?.registry_accessType === PRIVATE_REGISTRY) {
      registryFields.isRegistrySecretsRequired = true;
    }

    if (
      form?.registry_accessType === PRIVATE_REGISTRY &&
      form?.registry_secrets === REGISTRY_NEW_CREDENTIALS
    ) {
      registryFields.isRegistryCredUsernameRequired = true;
      registryFields.isRegistryCredPasswordRequired = true;
    }

    return {
      ...gitFields,
      ...registryFields,
    };
  };

  const onSave = () => {
    const formFields = getRequiredFields();
    const validationErrors = onValidateForm({
      ...form,
      ...formFields,
    });

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      showError(t.requiredMsg);
      return;
    }

    if (form?.isSubmitting) return;

    setForm((prev) => ({ ...prev, isSubmitting: true }));

    const triggerUpdate = (result, err, cb) => {
      if (!err) {
        setErrors({});
        setForm({ ...INITIAL_STATE_FORM, isSubmitting: false });
        cb && cb();
      } else {
        setForm((prev) => ({ ...prev, isSubmitting: false }));
        showError(err);
      }
    };

    if (data?.isEditForm) {
      updateWizard3(
        { currentName: data?.metadata?.name, ...form },
        (result, errorMsg) => {
          triggerUpdate(result, errorMsg, triggerUpdateTable);
        }
      );
    } else {
      setWizard3(form, (result, errorMsg) => {
        triggerUpdate(result, errorMsg, triggerUpdateTable);
      });
    }
  };

  const onToggleShowSecret = (key) => {
    setForm((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  let content;

  switch (stepState?.activeStep) {
    case 0:
      content = (
        <BasicsContent
          {...{ form, onChangeTxt, currentUser, onSelectOption, errors, clusters }}
        />
      );
      break;
    case 1:
      content = (
        <GitComponent
          {...{
            registryCredentials,
            form,
            onChangeTxt,
            onSelectOption,
            gitCredentials,
            onToggleShowSecret,
            errors,
          }}
        />
      );
      break;
    case 2:
      content = (
        <RegistryComponent
          {...{
            registryCredentials,
            onSelectOption,
            form,
            onChangeTxt,
            onToggleShowSecret,
            errors,
          }}
        />
      );
      break;
    case 3:
      content = (
        <>
          <ServiceComponent
            {...{
              form,
              onChangeTxt,
              onCheckboxChange,
              configs,
              onSelectMultipleOptions,
              onChangeTab,
              onChangeSubTxt,
              onSetConfigsAndSecrets,
              onOpenSecond,
              onShowPwd,
              onChangeTxtJson,
              onSetJsonVariable2Table,
              onHideModalSecond,
              onSelectOption,
              instancesCatalog,
              onChangeInstances,
              errors,
            }}
          />
        </>
      );
      break;
    default:
      content = (
        <>
          <Box>
            <span style={{ ...sectionLabel }}>{t.basicInfo}</span>

            <span style={{ ...styleQuestionLabel }}>
              {t.application + " " + t.name} :
            </span>{" "}
            <span style={{ ...styleQuestionAnswer }}>{form?.displayName}</span>

            { getEnv("REACT_APP_MULTICLUSTER_ENABLED") && (
              <>
                <br />
                <span style={{ ...styleQuestionLabel }}>
                  {t.targetKubernetesCluster} :
                </span>{" "}
                <span style={{ ...styleQuestionAnswer }}>{form?.cluster_name}</span>
              </>
            )}
          </Box>

          <Box>
            <span style={{ ...sectionLabel }}>{t.sourceInfo}</span>
            <span style={{ ...styleQuestionLabel }}>
              {t.git + " " + t.repository} :
            </span>{" "}
            <span style={{ ...styleQuestionAnswer }}>{form?.git_repo}</span>
            <br />
            <span style={{ ...styleQuestionLabel }}>
              {t.git + " " + t.revision} :
            </span>{" "}
            <span style={{ ...styleQuestionAnswer }}>{form?.git_revision}</span>
            <br />
            <span style={{ ...styleQuestionLabel }}>
              {t.git + " " + t.accessType} :
            </span>{" "}
            <span style={{ ...styleQuestionAnswer }}>
              {form?.git_accessType}
            </span>
            <br />
            {form?.git_accessType !== PUBLIC && (
              <>
                <span style={{ ...styleQuestionLabel }}>
                  {t.privateRepository} :
                </span>{" "}
                <span style={{ ...styleQuestionAnswer }}>
                  {form?.git_secrets}
                </span>
                <br />
                {form?.git_secrets === GIT_NEW_CREDENTIALS && (
                  <>
                    <span style={{ ...styleQuestionLabel }}>
                      {t.username} :
                    </span>{" "}
                    <span style={{ ...styleQuestionAnswer }}>
                      {form?.git_cred_username || ""}
                    </span>
                    <br />
                    <span style={{ ...styleQuestionLabel }}>
                      {t.password} :
                    </span>{" "}
                    <span style={{ ...styleQuestionAnswer }}>
                      {"*".repeat(form?.git_cred_password?.length || 0)}
                    </span>
                    <br />
                  </>
                )}
              </>
            )}
          </Box>

          <Box>
            <span style={{ ...sectionLabel }}>{t.dockerImage}</span>
            <span style={{ ...styleQuestionLabel }}>
              {`${t.registry} ${t.accessType}`} :
            </span>
            <span style={{ ...styleQuestionAnswer }}>
              {" "}
              {form?.registry_accessType}
            </span>
            <br />
            {form?.registry_accessType !== PLATFORM_REGISTRY && (
              <>
                <span style={{ ...styleQuestionLabel }}> {t.registry} :</span>{" "}
                <span style={{ ...styleQuestionAnswer }}>
                  {form?.registry_secrets}
                </span>
                <br />
                {form?.registry_secrets === REGISTRY_NEW_CREDENTIALS && (
                  <>
                    <span style={{ ...styleQuestionLabel }}>
                      {t.registry} {t.name} :
                    </span>{" "}
                    <span style={{ ...styleQuestionAnswer }}>
                      {form?.registry_name || ""}
                    </span>
                    <br />
                    <span style={{ ...styleQuestionLabel }}>
                      {t.server} :
                    </span>{" "}
                    <span style={{ ...styleQuestionAnswer }}>
                      {form?.registry_cred_server || ""}
                    </span>
                    <br />
                    {form?.registry_cred_server?.includes(".amazonaws.com") && (
                      <>
                        <span style={{ ...styleQuestionLabel }}>
                          {t.region} :
                        </span>{" "}
                        <span style={{ ...styleQuestionAnswer }}>
                          {form?.registry_cred_region || ""}
                        </span>
                        <br />
                      </>
                    )}
                    <span style={{ ...styleQuestionLabel }}>
                      {t.username} :
                    </span>{" "}
                    <span style={{ ...styleQuestionAnswer }}>
                      {form?.registry_cred_username || ""}
                    </span>
                    <br />
                    <span style={{ ...styleQuestionLabel }}>
                      {t.password} :
                    </span>{" "}
                    <span style={{ ...styleQuestionAnswer }}>
                      {"*".repeat(form?.registry_cred_password?.length || 0)}
                    </span>
                    <br />
                  </>
                )}
              </>
            )}
          </Box>

          <Box>
            <span style={{ ...sectionLabel }}>{t.applicationInfo}</span>
            <span style={{ ...styleQuestionLabel }}>
              {t.deployAutomatically} :{" "}
            </span>
            <span style={{ ...styleQuestionAnswer }}>
              {form.deployAutomatically ? "ON" : "OFF"}
            </span>
            <br />
            {form.deployAutomatically && (
              <>
                <span style={{ ...styleQuestionLabel }}>
                  {t.containerPort} :
                </span>{" "}
                <span style={{ ...styleQuestionAnswer }}>
                  {form?.container_port}
                </span>
                <br />
              </>
            )}
            <span style={{ ...styleQuestionLabel }}>
              {t.configsAndSecrets} :
            </span>
            <span style={{ ...styleQuestionAnswer }}>
              {" "}
              {JSON.stringify(form.configs_values)}
            </span>
            <br />
            <span style={{ ...styleQuestionLabel }}>{t.scaleTo0} :</span>
            <span style={{ ...styleQuestionAnswer }}>
              {" "}
              {form.scaleTo0 ? "ON" : "OFF"}
            </span>
          </Box>
        </>
      );
      break;
  }

  if (isLoading) {
    return <Loader />;
  }

  const isLastStep = stepState?.activeStep === steps.length - 1;
  const saveBtnTxt = isLastStep ? (
    form?.isSubmitting ? (
      <Loader
        specificDetails={{
          size: 24,
          style: loaderStyle,
        }}
      />
    ) : data?.isEditForm ? (
      t.update
    ) : (
      t.create
    )
  ) : data?.isEditForm ? (
    t.reviewPlusUpdate
  ) : (
    t.reviewPlusCreate
  );

  return (
    <>
      <Stepper
        nonLinear
        activeStep={stepState?.activeStep}
        sx={{ ...styleSteps, gap: "0.5rem" }}
      >
        {steps.map((label, index) => (
          <Step key={label} sx={{ position: "relative" }}>
            <StepButton
              sx={{
                ...styleStepBtn,
                "&:hover": {
                  opacity: 0.6,
                },
              }}
              onClick={() => onStep(index)}
            >
              {label}
            </StepButton>

            {/* Progress Bar */}
            <LinearProgress
              variant="determinate"
              value={
                stepState?.activeStep === index ? stepState?.progress : 100
              }
              sx={{
                position: "absolute",
                bottom: "-10px",
                left: 0,
                width: "100%",
                height: "6px",
                borderRadius: "6px",
                backgroundColor:
                  stepState?.activeStep === index ? "transparent" : "#f9f9f9",
                "& .MuiLinearProgress-bar": {
                  borderRadius: "6px",
                  background:
                    stepState?.activeStep === index
                      ? "linear-gradient(90deg, #007bff, #00aaff)"
                      : "#f9f9f9",
                  transition:
                    stepState?.activeStep === index
                      ? "width 0.4s ease-out"
                      : "none",
                },
              }}
            />
          </Step>
        ))}
      </Stepper>

      {/* Content with Fade-In Transition */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          minHeight: "calc(100% - 6em)",
          gap: "1rem",
          padding: "1rem",
          border: "1px solid #ddd",
          borderRadius: "8px",
          backgroundColor: "#f9f9f9",
          opacity: stepState?.opacity, // Control opacity based on progress
          transition:
            stepState?.progress > 10 ? "opacity 1s ease-in-out" : "none", // Gradual transition for opacity
        }}
      >
        {content}
      </div>

      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          position: "sticky",
          bottom: 0,
          background: "#fff",
          padding: "1rem 0",
          zIndex: 10,
        }}
      >
        <Button
          variant="outlined"
          sx={{ mr: 1, textTransform: "none" }}
          onClick={handleClose}
        >
          {t.cancel}
        </Button>
        {stepState?.activeStep !== 0 && (
          <Button
            variant="outlined"
            color="inherit"
            onClick={onStepBack}
            sx={{ mr: 1, textTransform: "none", marginLeft: "auto" }}
          >
            {t.previous}
          </Button>
        )}
        {steps.length > stepState?.activeStep + 1 && (
          <Button
            variant="outlined"
            color="inherit"
            onClick={onStepNext}
            sx={{
              mr: 1,
              textTransform: "none",
              ...(stepState?.activeStep === 0 ? { marginLeft: "auto" } : {}),
            }}
          >
            {t.next}: {steps[stepState?.activeStep + 1]}
          </Button>
        )}
        <Button
          variant="contained"
          sx={{ textTransform: "none" }}
          onClick={() => (!isLastStep ? onStep(steps.length - 1) : onSave())}
        >
          {saveBtnTxt}
        </Button>
      </Box>
    </>
  );
};

export default withErrorHandling(ConfigureBuild);
